import { AnimatePresence, motion } from 'framer-motion';
import React, { useContext, useEffect, useState, useMemo } from 'react';
import Container from 'react-bootstrap/Container';
import Navbar from 'react-bootstrap/Navbar';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Drawer } from 'vaul';
import {
    Mail,
    Phone,
    Home,
    Languages,
    Youtube,
    Twitter,
    Instagram,
    Linkedin,
    X,
    Menu
} from 'lucide-react';
import img from '../../assets/Img';
import { FetchDataContext } from '../../context/FetchData';
import { useLanguage } from '../../hooks/useLanguage';
import { translateRoute } from '../../utils/routeTranslator';
import './navbar.scss';
import routes from './routes';
import Icons from '../../constants/Icons';

const NavBar = ({ navbarColor }) => {
    const { t, i18n } = useTranslation();
    const { settings } = useContext(FetchDataContext);
    const { changeLanguage, isEnglish } = useLanguage();
    const location = useLocation();
    const isLang = i18n.language;

    const [scrollPosition, setScrollPosition] = useState(0);
    const [drawerOpen, setDrawerOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrollPosition(window.scrollY);
        };

        // Check initial scroll position
        setScrollPosition(window.scrollY);

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(() => {
        // Close drawer when route changes
        setDrawerOpen(false);
    }, [location.pathname]);

    // Prevent body scroll when drawer is open on mobile
    useEffect(() => {
        if (drawerOpen && window.innerWidth <= 992) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [drawerOpen]);

    const toggleDrawer = () => {
        setDrawerOpen(!drawerOpen);
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
    };

    const handleLocalizationChange = (targetLang) => {
        changeLanguage(targetLang);
        closeDrawer();
    };

    // Active state detection function
    const isRouteActive = useMemo(() => {
        const currentPath = location.pathname;

        const normalizePath = (path) => {
            if (!path) return '/';
            try {
                const decoded = decodeURIComponent(path);
                return decoded.replace(/\/+$/, '') || '/';
            } catch {
                return path.replace(/\/+$/, '') || '/';
            }
        };

        const normalizedCurrent = normalizePath(currentPath);

        return (routePath) => {
            if (!routePath) return false;

            const normalizedRoute = normalizePath(routePath);

            // Exact match
            if (normalizedCurrent === normalizedRoute) {
                return true;
            }

            // Special handling for home routes
            if (normalizedRoute === '/en' || normalizedRoute === '/ar') {
                return normalizedCurrent === '/en' || normalizedCurrent === '/ar';
            }

            // For other routes, check if current path starts with route path
            if (normalizedCurrent.startsWith(normalizedRoute + '/')) {
                return true;
            }

            // Handle URL-encoded paths
            try {
                const encodedRoute = encodeURI(normalizedRoute);
                if (normalizedCurrent === encodedRoute ||
                    normalizedCurrent.startsWith(encodedRoute + '/')) {
                    return true;
                }
            } catch {
                // Ignore encoding errors
            }

            try {
                const encodedCurrent = encodeURI(normalizedCurrent);
                if (encodedCurrent === normalizedRoute ||
                    encodedCurrent.startsWith(normalizedRoute + '/')) {
                    return true;
                }
            } catch {
                // Ignore encoding errors
            }

            return false;
        };
    }, [location.pathname]);

    const showTopBar = scrollPosition <= 400;

    return (
        <>
            {/* Top Social Bar */}
            <AnimatePresence>
                {showTopBar && (
                    <motion.div
                        dir={`${isLang === "en" ? 'ltr' : 'rtl'}`}
                        className="navbar__top-bar"
                        initial={{ opacity: 0, y: -50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -50 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                        <Container fluid className="navbar__top-container">
                            {/* Contact Links */}
                            <div className="top-bar__contact">
                                {settings?.email && (
                                    <motion.a
                                        href={`mailto:${settings.email}`}
                                        className="d-flex align-items-center gap-2"
                                        whileHover={{ y: -2, scale: 1.1 }}
                                        whileTap={{ scale: 0.95 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <Mail size={16} color='#fff' strokeWidth={1.5} />
                                        <div className="top-bar__link">
                                            <span className="top-bar__link-text">{settings.email}</span>
                                        </div>
                                    </motion.a>
                                )}
                                {settings?.whats && (
                                    <motion.a
                                        href={`tel:${settings.whats}`}
                                        className="d-flex align-items-center gap-2"
                                        whileHover={{ y: -2, scale: 1.1 }}
                                        whileTap={{ scale: 0.95 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <Phone size={16} color='#fff' strokeWidth={1.5} />
                                        <div className="top-bar__link">
                                            <span className="top-bar__link-text" dir='ltr'>{settings.whats}</span>
                                        </div>
                                    </motion.a>
                                )}
                            </div>

                            {/* Center - Home & Language */}
                            <div className="top-bar__center">
                                {/* <motion.div
                                    whileHover={{ scale: 1.15 }}
                                    whileTap={{ scale: 0.9 }}
                                >
                                    <Link to={isLang === 'en' ? '/en' : '/ar'} className="">
                                        <Home strokeWidth={1.5} color='#fff' size={18} />
                                    </Link>
                                </motion.div>
                                <div
                                    className="top-bar__lang-switcher"
                                    onClick={() => handleLocalizationChange(isEnglish ? 'ar' : 'en')}
                                >
                                    <Languages />
                                    <span>{isEnglish ? t('common.arabic') : t('common.english')}</span>
                                </div> */}
                            </div>

                            {/* Social Media Links */}
                            <div className="top-bar__social d-flex align-items-center gap-4                                                                                                             ">
                                {settings?.youtube && (
                                    <motion.a
                                        href={settings.youtube}
                                        target='_blank'
                                        rel="noreferrer"
                                        whileHover={{ scale: 1.2, y: -2 }}
                                        whileTap={{ scale: 0.9 }}
                                    >
                                        <Youtube size={18} color='#fff' strokeWidth={1.5} />
                                    </motion.a>
                                )}
                                {settings?.twitter && (
                                    <motion.a
                                        href={settings.twitter}
                                        target='_blank'
                                        rel="noreferrer"
                                        className="top-bar__social-link"
                                        whileHover={{ scale: 1.2, y: -2 }}
                                        whileTap={{ scale: 0.9 }}
                                    >
                                        <Icons.Twitter size={18} color='#fff' strokeWidth={1.5} />
                                    </motion.a>
                                )}
                                {settings?.instagram && (
                                    <motion.a
                                        href={settings.instagram}
                                        target='_blank'
                                        rel="noreferrer"
                                        whileHover={{ scale: 1.2, y: -2 }}
                                        whileTap={{ scale: 0.9 }}
                                    >
                                        <Instagram size={18} color='#fff' strokeWidth={1.5} />
                                    </motion.a>
                                )}
                                {settings?.linked_in && (
                                    <motion.a
                                        href={settings.linked_in}
                                        target='_blank'
                                        rel="noreferrer"
                                        whileHover={{ scale: 1.2, y: -2 }}
                                        whileTap={{ scale: 0.9 }}
                                    >
                                        <Linkedin size={18} color='#fff' strokeWidth={1.5} />
                                    </motion.a>
                                )}
                            </div>
                        </Container>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Main Navigation */}
            <div className={`main-nav position-relative  w-100 ${!showTopBar ? 'top-bar-hidden' : ''}`} style={{ top: showTopBar ? '0px' : '0px' }}>
                <Navbar
                    dir={`${isLang === "en" ? 'ltr' : 'rtl'}`}
                    fixed="top"
                    expand="lg"
                    className={`modern-navbar ${navbarColor} ${scrollPosition > 400 ? 'scrolled  bg-red w-100 position-fixed ' : ''}`


                    }
                >
                    <Container fluid className="navbar-container flex justify-content-between align-items-center"  >
                        {/* Mobile Menu Toggle - Left */}
                        <button
                            className={`navbar__toggle ${drawerOpen ? 'active' : ''}`}
                            onClick={toggleDrawer}
                            aria-label="Toggle menu"
                            aria-expanded={drawerOpen}
                        >
                            {drawerOpen ? (
                                <X size={24} color="#000" strokeWidth={1.5} />
                            ) : (
                                <Menu size={24} color="#000" strokeWidth={1.5} />
                            )}
                        </button>

                        {/* Logo - Center/Right */}
                        <div className="navbar__logo-wrapper">
                            <Navbar.Brand
                                className='navbar__logo'
                                as={Link}
                                to={isLang === 'en' ? '/en' : '/ar'}
                            >
                                <motion.img
                                    src={img.LogoMainColor}
                                    alt="Right Click Logo"
                                    whileHover={{ scale: 1.05 }}
                                    transition={{ duration: 0.2 }}
                                />
                            </Navbar.Brand>
                        </div>


                        {/* Navigation Links - Center */}
                        <div className={`navbar__menu ${drawerOpen ? 'active' : ''}`}>
                            <nav className="navbar__nav">
                                {routes?.map((route, index) => {
                                    const routePath = isLang === 'en'
                                        ? route.path
                                        : translateRoute(route.path, 'ar');
                                    const isActive = isRouteActive(routePath);

                                    return (
                                        <motion.div
                                            key={index}
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                        >
                                            <Link
                                                to={routePath}
                                                className={`navbar__link ${isActive ? 'active' : ''}`}
                                                onClick={closeDrawer}
                                            >
                                                <span className="navbar__link-text">
                                                    {isLang === "en" ? route.nameEn : route.nameAr}
                                                </span>
                                                <span
                                                    className="navbar__link-indicator"
                                                />
                                            </Link>
                                        </motion.div>
                                    );
                                })}
                            </nav>
                        </div>
                        {/* Desktop Language Switcher - Left */}
                        <div className="navbar__lang-section">
                            <button
                                className="lang__btn"
                                onClick={() => handleLocalizationChange(isEnglish ? 'ar' : 'en')}
                            >
                                <Icons.LangOutline />
                                <span>{isEnglish ? t('common.arabic') : t('common.english')}</span>
                            </button>
                        </div>
                    </Container>
                </Navbar>

                {/* Mobile Drawer Sidebar */}
                <Drawer.Root open={drawerOpen} onOpenChange={setDrawerOpen} direction={isLang === 'ar' ? 'right' : 'left'}>
                    <Drawer.Portal>
                        <Drawer.Overlay className="navbar__overlay" />
                        <Drawer.Content
                            dir={`${isLang === "en" ? 'ltr' : 'rtl'}`}
                            className="navbar__drawer"
                        >
                            {/* Drawer Header */}
                            <div className="drawer__header">
                                <Drawer.Close asChild>
                                    <button
                                        className="drawer__close-btn"
                                        aria-label="Close menu"
                                    >
                                        <X />
                                    </button>
                                </Drawer.Close>
                            </div>

                            {/* Drawer Menu Items */}
                            <nav className="drawer__nav">
                                {routes?.map((route, index) => {
                                    const routePath = isLang === 'en'
                                        ? route.path
                                        : translateRoute(route.path, 'ar');
                                    const isActive = isRouteActive(routePath);

                                    return (
                                        <div key={index}>
                                            <Link
                                                to={routePath}
                                                className={`drawer__link ${isActive ? 'active' : ''}`}
                                                onClick={closeDrawer}
                                            >
                                                <span className="drawer__link-icon">
                                                    {route.icon2 || route.icon}
                                                </span>
                                                <span className="drawer__link-text">
                                                    {isLang === "en" ? route.nameEn : route.nameAr}
                                                </span>
                                            </Link>
                                        </div>
                                    );
                                })}
                            </nav>

                            {/* Drawer Footer */}
                            <div className="drawer__footer">
                                {/* Language Switcher */}
                                <button
                                    className="drawer__lang-btn"
                                    onClick={() => handleLocalizationChange(isEnglish ? 'ar' : 'en')}
                                >
                                    <Languages />
                                    <span>{isEnglish ? t('common.arabic') : t('common.english')}</span>
                                </button>

                                {/* Social Media Links */}
                                <div className="drawer__social">
                                    <span className="drawer__social-title">{t('common.followUs')}</span>
                                    <div className="drawer__social-links">
                                        {settings?.youtube && (
                                            <a
                                                href={settings.youtube}
                                                target='_blank'
                                                rel="noreferrer"
                                                className="drawer__social-link"
                                            >
                                                <Youtube />
                                            </a>
                                        )}
                                        {settings?.twitter && (
                                            <a
                                                href={settings.twitter}
                                                target='_blank'
                                                rel="noreferrer"
                                                className="drawer__social-link"
                                            >
                                                <Icons.Twitter size={18} color='#fff' strokeWidth={1.5} />
                                            </a>
                                        )}
                                        {settings?.instagram && (
                                            <a
                                                href={settings.instagram}
                                                target='_blank'
                                                rel="noreferrer"
                                                className="drawer__social-link"
                                            >
                                                <Instagram />
                                            </a>
                                        )}
                                        {settings?.linked_in && (
                                            <a
                                                href={settings.linked_in}
                                                target='_blank'
                                                rel="noreferrer"
                                                className="drawer__social-link"
                                            >
                                                <Linkedin />
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Drawer.Content>
                    </Drawer.Portal>
                </Drawer.Root>
            </div>
        </>
    );
};

export default NavBar;
